import React, { useState, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import GambarResultPage from './GambarResultPage'
import { generateImage } from './gambarUtils'
import { AgentHistoryIntegration } from '../../utils/agentHistoryIntegration'
import { fileStorageService } from '../../services/fileStorageService'
import { useAuth } from '../../hooks/useSupabaseAuth.jsx'
import { useSubscription } from '../../hooks/useSubscription'
import { useCreditWarning } from '../../hooks/useCreditWarning'
import CreditWarningModal from '../../components/CreditWarningModal'
import { AGENT_TYPES } from '../index.js'

// Gambar Agent States
const GAMBAR_STATES = {
  GENERATING: 'generating',
  COMPLETED: 'completed'
}

const GambarAgentContent = ({ query, onBack, llmService, options }) => {
  const { user } = useAuth()
  const { consumeCredits } = useSubscription()
  const { warningModal, checkAndWarnCredits, closeWarning } = useCreditWarning()
  const [currentState, setCurrentState] = useState(GAMBAR_STATES.GENERATING)
  const [generatedImage, setGeneratedImage] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [aspectRatio, setAspectRatio] = useState(options?.aspectRatio || '1:1')
  const hasInitialized = useRef(false)
  const isGenerating = useRef(false)
  const hasCompletedGeneration = useRef(false) // Track if generation has ever completed successfully
  
  // Check if we already have a result for this query in sessionStorage
  React.useEffect(() => {
    const sessionKey = `gambar_result_${query}_${aspectRatio}`
    const cachedResult = sessionStorage.getItem(sessionKey)
    
    if (cachedResult) {
      try {
        const parsedResult = JSON.parse(cachedResult)
        console.log('📋 Loading cached image result for query:', query)
        setGeneratedImage(parsedResult)
        setCurrentState(GAMBAR_STATES.COMPLETED)
        hasCompletedGeneration.current = true
        setIsLoading(false)
        return
      } catch (error) {
        console.warn('⚠️ Failed to parse cached result, will generate new image')
        sessionStorage.removeItem(sessionKey)
      }
    }
    
    // Only generate if we don't have cached result and haven't initialized yet
    if (query && !hasInitialized.current && !isGenerating.current) {
      hasInitialized.current = true
      handleStartGeneration()
    }
  }, [query, aspectRatio]) // Include query and aspectRatio in dependencies

  const handleStartGeneration = useCallback(async (customAspectRatio = aspectRatio) => {
    // Prevent multiple simultaneous generations
    if (isGenerating.current) {
      console.log('⚠️ Generation already in progress, skipping...')
      return
    }
    
    // Additional check: if we already have a completed image, don't generate again
    if (currentState === GAMBAR_STATES.COMPLETED && generatedImage) {
      console.log('⚠️ Image already generated and completed, skipping...')
      return
    }
    
    // Strong protection: if generation has ever completed successfully, don't generate again
    if (hasCompletedGeneration.current) {
      console.log('⚠️ Generation has already completed successfully before, skipping...')
      return
    }
    

    
    isGenerating.current = true
    setCurrentState(GAMBAR_STATES.GENERATING)
    setIsLoading(true)
    setError(null)
    setGeneratedImage(null)
    
    try {
      // Check credits before starting generation with timeout and fallback
      let canProceed = true
      try {
        const creditCheckPromise = checkAndWarnCredits(AGENT_TYPES.GAMBAR)
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Credit check timeout')), 5000)
        )
        
        canProceed = await Promise.race([creditCheckPromise, timeoutPromise])
      } catch (creditError) {
        console.warn('⚠️ Credit check failed, proceeding with generation:', creditError.message)
        // Continue with generation even if credit check fails
        canProceed = true
      }
      
      if (!canProceed) {
        setIsLoading(false)
        return // Stop if insufficient credits
      }

      console.log(`🔄 Memulai generasi gambar untuk: ${query}`)
      
      const imageResult = await generateImage(query, options?.aspectRatio || customAspectRatio)
      
      // Simpan gambar ke Supabase storage jika user login
      let finalImageResult = imageResult
      if (user && imageResult.url) {
        try {
          console.log('🔄 Menyimpan gambar ke Supabase storage...')
          const storageResult = await fileStorageService.saveImageFromUrl(
            user.id,
            imageResult.url,
            query,
            {
              aspectRatio: customAspectRatio,
              width: imageResult.width,
              height: imageResult.height,
              seed: imageResult.seed
            }
          )
          
          if (storageResult.data) {
            // Update image result dengan URL Supabase yang permanen
            finalImageResult = {
              ...imageResult,
              url: storageResult.data.publicUrl,
              supabaseUrl: storageResult.data.publicUrl,
              originalUrl: imageResult.url,
              fileName: storageResult.data.fileName
            }
            console.log('✅ Gambar berhasil disimpan ke Supabase storage')
          } else {
            console.warn('⚠️ Gagal menyimpan ke Supabase, menggunakan URL asli')
          }
        } catch (storageError) {
          console.error('❌ Error menyimpan gambar ke storage:', storageError)
          // Tetap lanjutkan dengan URL asli jika gagal menyimpan
        }
      }
      
      setGeneratedImage(finalImageResult)
      
      // Cache the result in sessionStorage to prevent regeneration on page revisit
      const sessionKey = `gambar_result_${query}_${customAspectRatio}`
      try {
        sessionStorage.setItem(sessionKey, JSON.stringify(finalImageResult))
        console.log('💾 Image result cached for future visits')
      } catch (cacheError) {
        console.warn('⚠️ Failed to cache result:', cacheError)
      }
      
      console.log('🎉 Gambar berhasil dibuat')
      setCurrentState(GAMBAR_STATES.COMPLETED)
      hasCompletedGeneration.current = true // Mark as completed to prevent future generations
      
      // Consume credits after successful image generation
      try {
        await consumeCredits(AGENT_TYPES.GAMBAR, {
          query: query,
          agentType: AGENT_TYPES.GAMBAR,
          agentName: 'Gambar Agent',
          aspectRatio: customAspectRatio
        })
        console.log('✅ Credits consumed for Gambar Agent')
      } catch (creditError) {
        console.error('❌ Failed to consume credits for Gambar Agent:', creditError)
      }
      
      // Save to agent history
      try {
        await AgentHistoryIntegration.saveGambarResult({
          query: query,
          images: [finalImageResult],
          settings: {
            aspectRatio: customAspectRatio,
            ...options
          }
        })
        console.log('✅ Hasil gambar berhasil disimpan ke history')
      } catch (historyError) {
        console.error('❌ Error menyimpan ke history:', historyError)
      }
    } catch (err) {
      console.error('❌ Error dalam handleStartGeneration:', err)
      
      // Clear any cached result for this query since generation failed
      const sessionKey = `gambar_result_${query}_${customAspectRatio}`
      try {
        sessionStorage.removeItem(sessionKey)
        console.log('🗑️ Cleared cached result due to generation error')
      } catch (cacheError) {
        console.warn('⚠️ Failed to clear cache after error:', cacheError)
      }
      
      // Provide user-friendly error messages
      let userMessage = err.message
      if (err.message.includes('API Key')) {
        userMessage = 'Konfigurasi API bermasalah. Silakan hubungi administrator.'
      } else if (err.message.includes('timeout')) {
        userMessage = 'Koneksi timeout. Silakan coba lagi dengan koneksi internet yang stabil.'
      } else if (err.message.includes('NetworkError') || err.message.includes('fetch')) {
        userMessage = 'Masalah koneksi internet. Periksa koneksi Anda dan coba lagi.'
      } else if (!err.message || err.message.trim() === '') {
        userMessage = 'Terjadi kesalahan tidak dikenal. Silakan coba lagi.'
      }
      
      setError(userMessage)
      setCurrentState(GAMBAR_STATES.GENERATING) // Keep in generating state to allow retry
      hasInitialized.current = false // Reset initialization flag on error to allow retry
    } finally {
      setIsLoading(false)
      isGenerating.current = false // Reset flag when generation completes
    }
  }, []) // Empty dependency array to prevent recreation on subscription updates

  const handleRegenerateImage = useCallback((newAspectRatio) => {
    // Clear cached results for agenth old and new aspect ratios
    const oldSessionKey = `gambar_result_${query}_${aspectRatio}`
    const newSessionKey = `gambar_result_${query}_${newAspectRatio}`
    
    try {
      sessionStorage.removeItem(oldSessionKey)
      sessionStorage.removeItem(newSessionKey)
      console.log('🗑️ Cleared cached results for regeneration')
    } catch (error) {
      console.warn('⚠️ Failed to clear cache:', error)
    }
    
    // Reset all generation flags for regeneration
    isGenerating.current = false
    hasCompletedGeneration.current = false // Allow regeneration
    hasInitialized.current = false // Reset initialization flag
    
    setAspectRatio(newAspectRatio)
    
    // Force regeneration by resetting state first
    setCurrentState(GAMBAR_STATES.GENERATING)
    setGeneratedImage(null)
    setError(null)
    
    console.log('🔄 Manual regeneration triggered with aspect ratio:', newAspectRatio)
    
    // Call generation with new aspect ratio
    handleStartGeneration(newAspectRatio)
  }, [query, aspectRatio])

  // Always show result page (no outline page needed)
  return (
    <>
      <GambarResultPage
        query={query}
        generatedImage={generatedImage}
        isLoading={isLoading}
        error={error}
        isCompleted={currentState === GAMBAR_STATES.COMPLETED}
        aspectRatio={aspectRatio}
        onBack={onBack}
        onRegenerateImage={handleRegenerateImage}
      />
      
      {/* Credit Warning Modal */}
      <CreditWarningModal
        isOpen={warningModal.isOpen}
        onClose={closeWarning}
        actionType={warningModal.actionType}
        creditsRequired={warningModal.creditsRequired}
        creditsRemaining={warningModal.creditsRemaining}
        packageName={warningModal.packageName}
        isFreeUser={warningModal.isFreeUser}
        message={warningModal.message}
      />
    </>
  )
}

// Main GambarAgent component
const GambarAgent = (props) => {
  return <GambarAgentContent {...props} />
}

GambarAgent.propTypes = {
  query: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  llmService: PropTypes.object,
  options: PropTypes.object
}

GambarAgentContent.propTypes = {
  query: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  llmService: PropTypes.object,
  options: PropTypes.object
}

export default GambarAgent